import { useContext } from "react"
import Loader from "../common/Loader.js"
import DateRangeFilter from "./components/DateRangeFilter.js"
import HubMultiSelect from "./components/HubMultiSelect.js"
import { AuthContext } from "../../context/AuthContext.js"
import { hasAttendanceAccess } from "../../utils/networkMode.js"
import { DashboardLayout, StatCard, AmountCard, SectionTable, PageHeader } from "../common/page_component/index.js"
import ScanQrStatusButton from "../shipment-qr-status/ScanQrStatusButton.js"
import QrScanNoticeControl from "../qr-scan-notice/QrScanNoticeControl.js"
import { Users, Clock, Wallet } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { useOperationsDashboardData } from "./components/useOperationsDashboardData.js"
import OperationsDashboardView from "./components/OperationsDashboardView.js"
import RefreshingBanner from "./components/RefreshingBanner.js"

const AccountantDashboard = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext)
  const dash = useOperationsDashboardData({ includeUsersVehicles: true })

  const companyAccess = user?.company_features || user?.company_can_access || {};
  const attendanceEnabled = hasAttendanceAccess(user);
  const isAttendanceOnly = attendanceEnabled
    && !companyAccess.shipment
    && !companyAccess.commission
    && !companyAccess.external_commission
    && !companyAccess.on_contract
    && !companyAccess.on_contract_truck;

  const headerRight = (
    <>
      <div className="flex items-center gap-2">
        <QrScanNoticeControl />
        <ScanQrStatusButton mobileIconOnly />
      </div>
      <div className="dashboard-header-filters grid grid-cols-2 gap-2 min-w-0 w-full md:flex md:flex-wrap md:w-auto [&>*]:min-w-0 [&>:only-child]:col-span-2">
        {dash.showHubFilter && (
          <HubMultiSelect
            compact
            hubs={dash.hubs}
            value={dash.hubIds}
            onChange={dash.setHubIds}
            placeholder={t('dashboard.allHubs')}
            className="min-w-0 w-full md:w-auto"
          />
        )}
        <DateRangeFilter
          value={dash.dateRange} onChange={dash.setDateRange}
          customStart={dash.customStart} customEnd={dash.customEnd}
          onCustomStartChange={dash.setCustomStart} onCustomEndChange={dash.setCustomEnd}
        />
      </div>
    </>
  );

  if (dash.loading) return (
    <DashboardLayout>
      <Loader fullScreen={false} message={t('dashboard.loading')} />
    </DashboardLayout>
  )

  const ai = dash.attendanceData?.insights || {}
  const sai = dash.salaryData?.insights || {}

  if (isAttendanceOnly) {
    return (
      <DashboardLayout>
        <PageHeader title={t('dashboard.welcomeAccountant')}>
          <QrScanNoticeControl />
        <ScanQrStatusButton mobileIconOnly />
          <DateRangeFilter
            value={dash.dateRange} onChange={dash.setDateRange}
            customStart={dash.customStart} customEnd={dash.customEnd}
            onCustomStartChange={dash.setCustomStart} onCustomEndChange={dash.setCustomEnd}
          />
        </PageHeader>
        <RefreshingBanner show={dash.refreshing} />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          <StatCard label={t('dashboard.totalRecords')} value={ai.total_records || 0} icon={<Clock className="w-5 h-5 text-white" />} color="bg-blue-600" />
          <StatCard label={t('dashboard.uniqueUsers')} value={ai.unique_users || 0} icon={<Users className="w-5 h-5 text-white" />} color="bg-green-600" />
          <StatCard label={t('dashboard.currentlyLoggedIn')} value={ai.currently_logged_in || 0} icon={<Users className="w-5 h-5 text-white" />} color="bg-emerald-600" />
          <StatCard label={t('dashboard.totalWorkHours')} value={ai.total_work_hours || '0h 0m'} icon={<Clock className="w-5 h-5 text-white" />} color="bg-purple-500" />
          <StatCard label={t('dashboard.avgWorkHours')} value={ai.avg_work_hours || '0h 0m'} icon={<Clock className="w-5 h-5 text-white" />} color="bg-indigo-500" />
        </div>
        {sai.total_entries > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 mt-6">
            <AmountCard label={t('dashboard.totalNetSalary')} amount={sai.total_net_salary} icon={<Wallet className="w-5 h-5 text-white" />} color="bg-green-600" />
            <AmountCard label={t('dashboard.totalBasic')} amount={sai.total_basic_salary} icon={<Wallet className="w-5 h-5 text-white" />} color="bg-blue-600" />
            <AmountCard label={t('dashboard.totalAllowances')} amount={sai.total_allowances} icon={<Wallet className="w-5 h-5 text-white" />} color="bg-purple-500" />
            <AmountCard label={t('dashboard.totalDeductions')} amount={sai.total_deductions} icon={<Wallet className="w-5 h-5 text-white" />} color="bg-red-500" />
          </div>
        )}
      </DashboardLayout>
    )
  }

  return (
    <OperationsDashboardView
      title={t('dashboard.welcomeAccountant')}
      headerRight={headerRight}
      showHubFilter={dash.showHubFilter}
      hubs={dash.hubs}
      hubIds={dash.hubIds}
      onHubIdsChange={dash.setHubIds}
      cargoHubIds={dash.cargoHubIds}
      onCargoHubIdsChange={dash.setCargoHubIds}
      bookingHubIds={dash.bookingHubIds}
      onBookingHubIdsChange={dash.setBookingHubIds}
      shipmentHubIds={dash.shipmentHubIds}
      onShipmentHubIdsChange={dash.setShipmentHubIds}
      cargoData={dash.cargoData}
      bookingSummary={dash.bookingSummary}
      bookingMetric={dash.bookingMetric}
      onBookingMetricChange={dash.setBookingMetric}
      onSummaryDateChange={dash.setSummaryDate}
      cargoLoading={dash.cargoLoading}
      bookingLoading={dash.bookingLoading}
      commissionLoading={dash.commissionLoading}
      onContractLoading={dash.onContractLoading}
      tripLoading={dash.tripLoading}
      refreshing={dash.refreshing}
      mainRefreshing={dash.mainRefreshing}
      showCommissionAgentFilter={dash.showCommissionAgentFilter}
      commissionAgentId={dash.commissionAgentId}
      onCommissionAgentChange={dash.setCommissionAgentId}
      onContractVehicleId={dash.onContractVehicleId}
      onOnContractVehicleChange={dash.setOnContractVehicleId}
      tripVehicleId={dash.tripVehicleId}
      onTripVehicleChange={dash.setTripVehicleId}
      tripPartyId={dash.tripPartyId}
      onTripPartyChange={dash.setTripPartyId}
      attendanceLoading={dash.attendanceLoading}
      attendanceUserId={dash.attendanceUserId}
      onAttendanceUserChange={dash.setAttendanceUserId}
      attendanceHubId={dash.attendanceHubId}
      onAttendanceHubChange={dash.setAttendanceHubId}
      salaryLoading={dash.salaryLoading}
      salaryUserId={dash.salaryUserId}
      onSalaryUserChange={dash.setSalaryUserId}
      salaryHubId={dash.salaryHubId}
      onSalaryHubChange={dash.setSalaryHubId}
      salaryMonth={dash.salaryMonth}
      onSalaryMonthChange={dash.setSalaryMonth}
      salaryYear={dash.salaryYear}
      onSalaryYearChange={dash.setSalaryYear}
      user={user}
      userData={dash.userData}
      vehicleData={dash.vehicleData}
      shipmentData={dash.shipmentData}
      commissionData={dash.commissionData}
      onContractData={dash.onContractData}
      tripContractData={dash.tripContractData}
      attendanceData={dash.attendanceData}
      salaryData={dash.salaryData}
      attendanceEnabled={attendanceEnabled}
      companyAccess={companyAccess}
      dateRange={dash.dateRange}
      customStart={dash.customStart}
      customEnd={dash.customEnd}
      customStart={dash.customStart}
      customEnd={dash.customEnd}
    />
  )
}

export default AccountantDashboard;
